import { memo } from 'react'
import styles from './RequisitionCardV2.module.sass'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import { useReload } from 'store'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import { RequisitionOptions } from './RequisitionCardActions/RequisitionCardActions'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

const RequisitionCardV2 = memo(
	(props) => {
		const { className, data, ...attributes } = props
		const labels = data.$cardLabels || {}
		const { showDialog, openFile, i18n } = useGlobal()
		const reload = useReload()
		const titles = [i18n`Actions`]
		const remainingMedicalActs = (data.medicalActs?.length || 0) - 2
		const remainingExams = (data.exams?.length || 0) - 2

		return (
			<>
				<HStack
					{...attributes}
					className={`card ${styles['root'] || ''} ${
						className || ''
					} ${data?.$highlight ? styles['highlighted'] : ''}`}
					justify='space-between'
					gap='small'
					columns={[1, 2, 1]}
					onClick={() => {
						data.$action && data.$action()
					}}
				>
					<VStack className={styles['requisition-title-section']}>
						<HStack className={styles['requisition-title']}>
							<h5>{data.type?.name}</h5>
							{data.hasDocuments && (
								<ModalOptions
									title={labels.documents}
									options={data.documentsOptions}
									centerArrow
									fixedHeight='auto'
									alwaysAbove
									selectable={false}
									onSelect={(e) => {
										openFile && openFile(e)
										return true
									}}
								>
									<button
										className={styles['attachments-button']}
									>
										<Icon
											className={styles['attachments']}
											name='attachment'
											width='20px'
											height='18px'
										/>
									</button>
								</ModalOptions>
							)}
						</HStack>
						<VStack>
							<small>
								{data.scheduledDate?.shortDateFormat ||
									data.requisitionDate?.shortDateFormat}{' '}
							</small>
							<small>
								{data.establishment?.name ||
									i18n`Without establishment`}
							</small>
						</VStack>
					</VStack>
					<VStack className={styles['requisition-medical-acts']}>
						{data?.type?.id !== 6 && data?.medicalActs?.length && (
							<>
								<small>
									<strong>
										{data?.medicalActs[0]?.code}
									</strong>{' '}
									- {''}
									{data?.medicalActs[0]?.name}
								</small>
								{remainingMedicalActs >= 0 && (
									<small>
										<strong>
											{data?.medicalActs[1]?.code}
										</strong>{' '}
										- {''}
										{data?.medicalActs[1]?.name}
									</small>
								)}
								{remainingMedicalActs > 0 && (
									<small>
										{remainingMedicalActs === 1
											? i18n`+${remainingMedicalActs} medical act`
											: i18n`+${remainingMedicalActs} medical acts`}
									</small>
								)}
							</>
						)}
						{data?.type?.id === 6 && data?.exams?.length && (
							<>
								<small>
									<strong>{data?.exams[0]?.code}</strong> -{' '}
									{''}
									{data?.exams[0]?.name}
								</small>
								{remainingExams >= 0 && (
									<small>
										<strong>{data?.exams[1]?.code}</strong>{' '}
										- {''}
										{data?.exams[1]?.name}
									</small>
								)}
								{remainingExams > 0 && (
									<small>
										{data.exams.length - 2 === 1
											? i18n`+${remainingExams} exam`
											: i18n`+${remainingExams} exams`}
									</small>
								)}
							</>
						)}
					</VStack>

					<VStack justify='space-around'>
						<ModalOptions
							titles={titles}
							groups={RequisitionOptions(
								i18n,
								data,
								showDialog,
								reload
							)}
							options={RequisitionOptions(
								i18n,
								data,
								showDialog,
								reload
							)}
							corner='straight'
							onSelect={(item) => {
								item && item.action && item.action()
							}}
						>
							<button className={styles['requisition-options']}>
								<Icon
									className={styles['more-icon']}
									width='32px'
									height='32px'
									name={'more'}
								/>
							</button>
						</ModalOptions>
						<VStack>
							<h5
								className={`${styles['requisition-status']} ${
									styles[`status-${data?.status?.icon}`]
								}`}
							>
								{data.status?.description || ''}
							</h5>
							<small className={styles['requisition-id']}>
								#
								<FuseHighlight
									matchedWords={
										data.$textHighlight?.matchedIndexes?.[
											'id'
										]
									}
									text={data.id}
								/>
							</small>
						</VStack>
					</VStack>
				</HStack>
			</>
		)
	},
	(prevProps, nextProps) =>
		prevProps.data.id === nextProps.data.id &&
		prevProps.data.medicalActs === nextProps.data.medicalActs &&
		prevProps.data.medicalActs?.lenght ===
			nextProps.data.medicalActs?.lenght &&
		prevProps.data.$detailLevel === nextProps.data.$detailLevel &&
		prevProps.data.$highlight === nextProps.data.$highlight
)
RequisitionCardV2.displayName = 'RequisitionCardV2'
RequisitionCardV2.defaultHeight = 68
RequisitionCardV2.defaultSummaryHeight = 68
RequisitionCardV2.defaultLines = 2

export default RequisitionCardV2

export const RequisitionCardV2Skeleton = (props) => {
	const { className, ...attributes } = props

	return (
		<div
			{...attributes}
			className={`card ${styles['root'] || ''} ${className || ''} ${
				styles['skeleton-card']
			}`}
		>
			<VStack className={styles['requisition-title-section']}>
				<h5>
					<Skeleton className={`${styles['skeleton']}`} />
				</h5>
				<small>
					<Skeleton className={`${styles['skeleton']}`} />
				</small>
			</VStack>

			<VStack justify='space-around'>
				<span>
					<Skeleton className={`${styles['skeleton']}`} />
				</span>
				<span>
					<Skeleton className={`${styles['skeleton']}`} />
				</span>
			</VStack>

			<VStack justify='space-around'>
				<h5>
					<Skeleton className={`${styles['skeleton']}`} />
				</h5>
				<small>
					<Skeleton
						className={`${styles['skeleton']} ${styles['small']}`}
					/>
				</small>
			</VStack>
		</div>
	)
}
RequisitionCardV2Skeleton.defaultHeight = 60
RequisitionCardV2Skeleton.verticalDefaultHeight = 60
